import { useMemo, useState } from "react";
import { format, isValid, parseISO } from "date-fns";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * On-theme date picker (navy/orange) storing ISO yyyy-mm-dd strings.
 * Props: value (ISO string), onChange(isoString), testid, placeholder, minDate
 */
export const DatePickerField = ({ value, onChange, testid, placeholder = "Pick a date", minDate }) => {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => {
    if (!value) return undefined;
    const d = parseISO(value);
    return isValid(d) ? d : undefined;
  }, [value]);

  const handleSelect = (day) => {
    if (!day) return;
    onChange(format(day, "yyyy-MM-dd"));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testid}
            className="field-input flex items-center justify-between gap-2 text-left w-full"
          >
            <span className={selected ? "text-cream" : "text-faded"}>
              {selected ? format(selected, "dd MMM yyyy") : placeholder}
            </span>
            <CalendarIcon size={15} className="text-gold shrink-0" />
          </button>
        </PopoverTrigger>
        {selected && (
          <button
            type="button"
            aria-label="Clear date"
            data-testid={testid ? `${testid}-clear` : undefined}
            onClick={() => onChange("")}
            className="absolute right-9 top-1/2 -translate-y-1/2 text-faded hover:text-gold transition-colors p-1"
          >
            <X size={13} />
          </button>
        )}
      </div>
      <PopoverContent
        align="start"
        data-testid={testid ? `${testid}-calendar` : undefined}
        className="w-auto p-0 rounded-2xl border border-gold/25 bg-card shadow-2xl shadow-black/50 z-[90]"
      >
        <Calendar
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          disabled={minDate ? { before: minDate } : undefined}
          defaultMonth={selected}
          classNames={{
            months: "p-4",
            month: "space-y-3",
            caption: "flex justify-center relative items-center",
            caption_label: "font-display text-base text-cream tracking-wide",
            nav: "flex items-center space-x-1",
            nav_button:
              "h-8 w-8 rounded-full border border-gold/30 text-gold bg-transparent hover:bg-gold hover:text-ink transition-colors flex items-center justify-center",
            nav_button_previous: "absolute left-1",
            nav_button_next: "absolute right-1",
            head_row: "flex",
            head_cell: "text-faded w-9 text-[11px] font-mono uppercase tracking-wider text-center",
            row: "flex w-full mt-1.5",
            cell: "relative p-0 text-center",
            day: "h-9 w-9 rounded-full text-sm text-mist hover:bg-gold/15 hover:text-gold transition-colors aria-selected:opacity-100",
            day_selected: "!bg-gold !text-ink font-semibold hover:!bg-gold-hover",
            day_today: "border border-gold/50 text-gold",
            day_outside: "text-faded/50",
            day_disabled: "text-faded/40 hover:bg-transparent hover:text-faded/40 cursor-not-allowed",
          }}
          components={{
            IconLeft: (props) => <ChevronLeft size={15} {...props} />,
            IconRight: (props) => <ChevronRight size={15} {...props} />,
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
